export function stackedCards() {
    gsap.registerPlugin(ScrollTrigger);

    const cards = document.querySelectorAll('.stacked-card__wrapper .stacked-card--item');
    const container = document.querySelector('.stacked-card__block');
    const header = document.querySelector('.header');
    
    // Check if screen size is above 992px
    const isDesktop = () => window.innerWidth > 992;
    
    // Store ScrollTrigger instances for cleanup
    const scrollTriggers = [];
    
    // Get header height
    const getHeaderHeight = () => {
        if (header) {
            return header.getBoundingClientRect().height;
        }
        return 0;
    };
    
    // Calculate optimal scroll distance for transitions
    const getScrollDistance = () => {
        const headerHeight = getHeaderHeight();
        const viewportHeight = window.innerHeight;
        // Use a reasonable scroll distance, not full viewport
        return Math.max(viewportHeight - headerHeight, 600); // Minimum 600px for smooth transition
    };
    
    // Set wrapper height based on scroll distance needed
    const setWrapperHeight = (wrapper, scrollDistance) => {
        wrapper.style.height = `${scrollDistance}px`;
    };
    
    // Reset wrapper heights (for mobile)
    const resetWrapperHeights = () => {
        wrappers.forEach((wrapper) => {
            wrapper.style.height = '';
        });
        if (container) {
            container.style.paddingBottom = '';
        }
    };
    
    // Reset card styles (for mobile)
    const resetCardStyles = () => {
        cards.forEach((card) => {
            gsap.set(card, { clearProps: 'all' });
            card.style.zIndex = '';
        });
    };
    
    // Clean up ScrollTrigger instances
    const cleanupScrollTriggers = () => {
        scrollTriggers.forEach(st => {
            if (st && st.kill) {
                st.kill();
            }
        });
        scrollTriggers.length = 0;
    };
    
    // Set height for all wrappers
    const wrappers = document.querySelectorAll('.stacked-card__wrapper');
    const scrollDistance = getScrollDistance();
    
    // Helper function to calculate last wrapper height based on card height
    const calculateLastWrapperHeight = (wrapper, card, baseScrollDistance) => {
        if (!container || !card) return baseScrollDistance;
        // Get the actual card height
        const cardHeight = card.offsetHeight;
        const spacingAfterCard = 160; // Space after last card before section end
        // Wrapper height: card height + spacing (so we can unpin at height - 130px)
        const calculatedHeight = cardHeight + spacingAfterCard;
        // But don't exceed container bounds
        const containerBottom = container.offsetTop + container.offsetHeight;
        const wrapperStart = wrapper.offsetTop;
        const maxHeight = containerBottom - wrapperStart;
        // Use calculated height or container limit, whichever is smaller
        return Math.min(calculatedHeight, maxHeight);
    };
    
    // Initialize animations
    const initAnimations = () => {
        // Clean up any existing ScrollTriggers
        cleanupScrollTriggers();
        
        if (!isDesktop()) {
            // Reset styles for mobile
            resetWrapperHeights();
            resetCardStyles();
            return;
        }
        
        // Set initial heights
        wrappers.forEach((wrapper, index) => {
            const isLastWrapper = index === wrappers.length - 1;
            if (isLastWrapper && container) {
                const lastCard = cards[cards.length - 1];
                const lastWrapperHeight = calculateLastWrapperHeight(wrapper, lastCard, scrollDistance);
                wrapper.style.height = `${lastWrapperHeight}px`;
                // Add padding-bottom to container to ensure spacing after last card
                container.style.paddingBottom = '160px';
            } else {
                setWrapperHeight(wrapper, scrollDistance);
            }
        });
        
        cards.forEach((card, i) => {
            const wrapper = card.closest('.stacked-card__wrapper');
            const isLastCard = i === cards.length - 1;
            
            // Position card at 80px from top and center horizontally using GSAP (smooth, no jumps)
            // This ensures GSAP controls all transforms for smooth pinning
            gsap.set(card, {
                top: 80, // Position at 80px from top
                left: "50%",
                xPercent: -50, // Center horizontally using GSAP's transform system
                y: 0 // No vertical offset initially
            });
            
            // Pin the card - positioned at 80px from top
            // Start pinning when wrapper reaches 80px from top
            const st = ScrollTrigger.create({
                trigger: wrapper,
                start: () => `top 180px`,
                end: isLastCard && container 
                    ? () => {
                        // For last card: unpin when wrapper height - 130px is scrolled
                        // This leaves 130px of space before the next section
                        const wrapperHeight = wrapper.offsetHeight;
                        const spacingAfterCard = 160;
                        const unpinHeight = wrapperHeight - spacingAfterCard;
                        return `+=${unpinHeight}`;
                    }
                    : () => `+=${scrollDistance}`,
                pin: card,
                pinSpacing: false,
                scrub: 1,
                invalidateOnRefresh: true
            });
            scrollTriggers.push(st);

            if (i > 0) {
                const prevCard = cards[i - 1];

                // Ensure previous card is below current
                prevCard.style.zIndex = 0;
                card.style.zIndex = 1;

                // Set transform origin to center to prevent jump
                gsap.set(prevCard, { transformOrigin: "center center" });

                // Gradually scale and fade previous card while scrolling
                const fadeSt = gsap.to(prevCard, {
                    scale: 0.8,       // scale down gradually
                    opacity: 0,       // fade out smoothly
                    ease: "none",
                    scrollTrigger: {
                        trigger: wrapper,
                        start: () => `top bottom+=50`,  // start fading slightly before wrapper reaches bottom
                        end: () => `top 80px`,           // end when wrapper reaches 80px from top
                        scrub: true
                    }
                });
                scrollTriggers.push(fadeSt.scrollTrigger);
            }
        });
    };
    
    // Update height on resize (single listener for all wrappers)
    const handleResize = () => {
        const wasDesktop = scrollTriggers.length > 0;
        const nowDesktop = isDesktop();
        
        // If crossing the 992px threshold, reinitialize animations
        if (wasDesktop !== nowDesktop) {
            initAnimations();
            return;
        }
        
        // Only update heights if desktop
        if (!nowDesktop) {
            return;
        }
        
        const newScrollDistance = getScrollDistance();
        wrappers.forEach((wrapper, index) => {
            const isLastWrapper = index === wrappers.length - 1;
            if (isLastWrapper && container) {
                const lastCard = cards[cards.length - 1];
                const lastWrapperHeight = calculateLastWrapperHeight(wrapper, lastCard, newScrollDistance);
                wrapper.style.height = `${lastWrapperHeight}px`;
                // Ensure padding-bottom is maintained on resize
                container.style.paddingBottom = '160px';
            } else {
                setWrapperHeight(wrapper, newScrollDistance);
            }
        });
        // Update card positions to maintain 80px from top
        cards.forEach((card) => {
            gsap.set(card, { top: 80 });
        });
        ScrollTrigger.refresh();
    };
    
    // Initialize on load
    initAnimations();
    
    // Handle resize
    window.addEventListener('resize', handleResize);
}
 